import { useRef, useCallback, PropsWithChildren, useImperativeHandle, forwardRef } from 'react';
import cn from 'classnames';
import * as css from './SwingThreadContainer.css';

const SWING_THREAD_CLASS_NAME = 'swingThreads';
const SWING_THREAD_ACTIVE_Z_INDEX = '5';
const SWING_THREAD_INACTIVE_Z_INDEX = '4';

export type SwingThreadContainerProps = {
    initialPositionX?: number;
    initialPositionY?: number;
    className?: string;
    headerNode: React.ReactNode;
};

export const SwingThreadContainer = forwardRef<HTMLElement, PropsWithChildren<SwingThreadContainerProps>>(
    ({ initialPositionX = 0, initialPositionY = 0, headerNode, children, className }, ref) => {
        const containerRef = useRef<HTMLElement>(null);
        const positionRef = useRef({ x: initialPositionX, y: initialPositionY });
        const offsetRef = useRef({ x: 0, y: 0 });

        useImperativeHandle(ref, () => containerRef.current as HTMLElement);

        const handleFocusThread = useCallback((e: React.PointerEvent<HTMLElement>) => {
            document.querySelectorAll<HTMLElement>(`article.${SWING_THREAD_CLASS_NAME}`).forEach((el) => {
                el.style.zIndex = SWING_THREAD_INACTIVE_Z_INDEX;
            });
            (e.currentTarget as HTMLElement).style.zIndex = SWING_THREAD_ACTIVE_Z_INDEX;
        }, []);

        const handlePointerDown = useCallback((e: React.PointerEvent<HTMLElement>) => {
            const target = e.target as HTMLElement;
            if (target.closest('div[role="button"]') || target.closest('button')) return;

            const el = containerRef.current;
            if (!el) return;

            el.setPointerCapture(e.pointerId);

            let frameId = 0;
            const rect = el.getBoundingClientRect();

            offsetRef.current = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            };

            const handlePointerMove = (moveEvt: PointerEvent) => {
                const x = moveEvt.clientX - offsetRef.current.x;
                const y = moveEvt.clientY - offsetRef.current.y;

                cancelAnimationFrame(frameId);
                frameId = requestAnimationFrame(() => {
                    positionRef.current = { x, y };
                    el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
                });
            };

            const handlePointerUp = () => {
                cancelAnimationFrame(frameId);
                el.releasePointerCapture(e.pointerId);
                window.removeEventListener('pointermove', handlePointerMove);
                window.removeEventListener('pointerup', handlePointerUp);
                window.removeEventListener('pointercancel', handlePointerUp);
            };
            window.addEventListener('pointermove', handlePointerMove);
            window.addEventListener('pointerup', handlePointerUp);
            window.addEventListener('pointercancel', handlePointerUp);
        }, []);

        return (
            <article
                ref={containerRef}
                onPointerDown={handleFocusThread}
                style={{
                    transform: `translate3d(${positionRef.current.x}px, ${positionRef.current.y}px, 0)`,
                }}
                className={cn(css.swingThreadContainer, SWING_THREAD_CLASS_NAME, className)}
            >
                <header className={css.header} onPointerDown={handlePointerDown}>
                    {headerNode}
                </header>

                {children}
            </article>
        );
    },
);

SwingThreadContainer.displayName = 'SwingThreadContainer';
